import { z } from 'zod';
import { BagPhotoPolicy } from '../bag-photos';
import {
  CurrencyCode,
  DeliveryWindow,
  ImageUrl,
  Instant,
  LocalTime,
  Ok,
  PageRequest,
  PublicId,
  pageOf,
  ServiceDay,
  TimeZone,
  Uuid,
} from '../common';
import { ItemAvailability } from '../enums';
import { defineEndpoint } from '../http';
import { patchSchema } from '../patch';
import { BakeryContext } from './device';

// ---------------------------------------------------------------------------
// Stock and service controls (PRD BAK-005, BAK-006)
// ---------------------------------------------------------------------------

export const listStock = defineEndpoint({
  method: 'GET',
  path: '/b/stock',
  mount: 'bakery',
  summary: 'Item availability for this location (BAK-005)',
  response: z.object({
    items: z.array(
      z.object({
        itemId: PublicId,
        name: z.string().max(120),
        /** The product's hero photo, so the counter matches by sight (CAT-002). */
        imageUrl: ImageUrl.nullable(),
        availability: ItemAvailability,
        soldOutUntil: Instant.nullable(),
      }),
    ),
  }),
});

export const setStock = defineEndpoint({
  method: 'POST',
  path: '/b/stock/:itemId',
  mount: 'bakery',
  summary: 'One-tap availability: in stock, sold out today, hidden indefinitely (BAK-005)',
  params: z.object({ itemId: PublicId }),
  body: z.object({ availability: ItemAvailability }),
  idempotent: true,
  response: Ok,
});

export const pauseOrders = defineEndpoint({
  method: 'POST',
  path: '/b/service/pause',
  mount: 'bakery',
  summary: 'Pause new orders for a duration or until resumed (BAK-006)',
  body: z.object({
    durationMinutes: z.int().positive().max(720).nullable(),
    resume: z.boolean().default(false),
  }),
  idempotent: true,
  response: BakeryContext,
});

// ---------------------------------------------------------------------------
// Manager portal (PRD BAK-010, BAK-011, BAK-014, BAK-027, BAK-028)
// ---------------------------------------------------------------------------

/**
 * ONE service: a span in which a courier may collect (PRD BAK-046).
 *
 * This used to be an opening rule with three times: `opensAt`, `closesAt` and
 * `earliestReadyAt`. Only the last was ever stored - the first was returned as a
 * copy of it and the second was a literal '13:00' the route handler invented -
 * so two of the three controls in the manager portal changed nothing at all.
 * What a bakery actually configures is the span in which a bag can be handed
 * over, so that is what the wire carries.
 */
export const PickupServiceRule = z.object({
  /** The first moment an order can be collected in this service. */
  earliestPickupAt: LocalTime,
  /** The last. Null leaves the service unbounded - legal only on the last one. */
  latestPickupAt: LocalTime.nullable(),
});
export type PickupServiceRule = z.infer<typeof PickupServiceRule>;

/**
 * A weekday's services (PRD BAK-046).
 *
 * It was ONE span until 2026-08-26, which could not describe a house that bakes
 * for the morning, shuts at one and reopens at four. The cap of two is the
 * SCREEN's rule rather than the platform's - the schema underneath is a list and
 * a third service would be a screen change with no migration behind it - but a
 * cap the contract does not state is a cap no client has to respect.
 *
 * `closed` is derived from the list being empty and is carried anyway: every
 * client already branches on it, and a boolean is cheaper to read on a counter
 * screen than a length check.
 */
export const PickupWindowRule = z.object({
  /** 1 = Monday ... 7 = Sunday, ISO-8601. */
  weekday: z.int().min(1).max(7),
  closed: z.boolean(),
  /** In chronological order. Empty closes the weekday. */
  services: z.array(PickupServiceRule).max(2),
});
export type PickupWindowRule = z.infer<typeof PickupWindowRule>;

export const Closure = z.object({
  id: Uuid,
  from: ServiceDay,
  to: ServiceDay,
});
export type Closure = z.infer<typeof Closure>;

/**
 * An order that closing a day would take, as the confirmation names it
 * (BAK-047 clause 5, amended 2026-09-03 and 2026-10-09).
 *
 * The code on the ticket and the morning it was promised for, and nothing else.
 * This shape is answered on the `/b` mount, where a counter is never shown what
 * a guest paid (BAK-029), so the dialog COUNTS orders and names them by the
 * code a baker would call one out by - it never prices them.
 */
export const ClosureAffectedOrder = z.object({
  orderId: Uuid,
  shortCode: z.string().min(4).max(10),
  promisedWindow: DeliveryWindow,
});
export type ClosureAffectedOrder = z.infer<typeof ClosureAffectedOrder>;

/**
 * The confirmation every closing write takes (BAK-047 clause 5, amended
 * 2026-10-09; #2155, #2182): the TOTAL of orders the caller was shown, not a
 * boolean. A baker told "3 orders" authorised taking three, and a dialog left
 * open while a fourth guest pays must not quietly take that fourth one down
 * too. The closing write counts again inside its own transaction and answers
 * `applied: false` on any mismatch, which re-opens the dialog on the new number
 * rather than acting on a number nobody read.
 *
 * Absent means "nobody has been told". That is the safe default: a caller that
 * never sends it simply cannot close a day carrying orders, which is the
 * direction this rule wants to fail in.
 */
export const ClosureConfirmation = z.object({
  total: z.int().min(0).max(500),
});
export type ClosureConfirmation = z.infer<typeof ClosureConfirmation>;

/**
 * The body every closure-day twin takes (`/b`, `/c` and `/internal`), stated
 * once so the three mounts cannot drift into three different confirmations.
 */
export const ClosureDayBody = z.object({
  day: ServiceDay,
  closed: z.boolean(),
  confirmOrders: ClosureConfirmation.optional(),
});
export type ClosureDayBody = z.infer<typeof ClosureDayBody>;

/**
 * The from/to form's body (BAK-010) on all three mounts, carrying the same
 * confirmation as the calendar's single tap.
 */
export const ClosureRangeBody = z.object({
  from: ServiceDay,
  to: ServiceDay,
  confirmOrders: ClosureConfirmation.optional(),
});
export type ClosureRangeBody = z.infer<typeof ClosureRangeBody>;

/**
 * The answer to one tap on the calendar, for all three mounts.
 *
 * `applied: false` is not an error and is not a failure: it is the platform
 * saying "this day carries these orders - ask, then tell me again". Nothing was
 * written, and `closures` is still the shop's whole current list, so the grid
 * repaints from this reply exactly as it does from a successful tap and the cell
 * the baker optimistically filled goes back to open on its own.
 *
 * `orders` is ONE list (BAK-047 clause 5, amended 2026-10-09; #2155): a closed
 * day refuses every order its counter holds that a rider has not collected - an
 * offer on any rung, and an order the counter had already accepted - through
 * the same refusal a rejected order takes, and the every-minute engine hands
 * each one to another house or, when nobody is left, ends it with the guest
 * refunded in full. An order whose bag has already been collected is in no
 * list: the promise was kept, and closing the rest of the day says nothing
 * about it.
 *
 * Both arms state every field (the `/c/analytics` lesson): an applied tap
 * answers the orders it took rather than omitting the key, so a caller
 * reading `orders.length` gets the total in every case.
 */
export const ClosureDayResult = z.object({
  applied: z.boolean(),
  closures: z.array(Closure),
  orders: z.array(ClosureAffectedOrder),
});
export type ClosureDayResult = z.infer<typeof ClosureDayResult>;

/**
 * The from/to form's answer (BAK-010), which carries the same confirmation for
 * the same reason.
 *
 * A fortnight in August recorded as a range takes exactly the orders a
 * fortnight of taps would, and a guard on the calendar alone would leave the
 * form beside it as a one-click way around the question. `closure` is null
 * whenever `applied` is false - there is no row to name, because none was
 * written.
 */
export const ClosureRangeResult = z.object({
  applied: z.boolean(),
  closure: Closure.nullable(),
  orders: z.array(ClosureAffectedOrder),
});
export type ClosureRangeResult = z.infer<typeof ClosureRangeResult>;

export const BakerySettings = z.object({
  locationId: Uuid,
  timeZone: TimeZone,
  pickupWindows: z.array(PickupWindowRule).length(7),
  closures: z.array(Closure),
  acceptanceMode: z.enum(['AUTO', 'MANUAL']),
  /**
   * BAK-003: minutes BEFORE the promised window's start by which a manually
   * accepting counter must have answered a pre-order. Default 60.
   */
  acceptanceTimeoutMinutes: z.int().positive().max(720),
  /**
   * BAK-003's reaction floor for orders arriving close to their slot, and the
   * response window immediate orders will get. Administered by operations -
   * the tablet shows it read-only, and the PATCH below cannot carry it.
   */
  immediateAcceptTimeoutMinutes: z.int().positive().max(60),
  /**
   * The order camera's setting for this shop. Read on both mounts; written
   * by the house on `/c` (`ShopSettingsPatch`) and by operations, NEVER by
   * the counter - `BakerySettingsPatch` omits it.
   */
  bagPhotoPolicy: BagPhotoPolicy,
  /**
   * The platform's instant when this was read (F7). The closure calendar's
   * "today" is the PLATFORM's day in the shop's zone - read through
   * `usePlatformNow` off this stamp - never the reader's own clock, so a
   * rehearsal a week ahead greys the days the server itself would refuse.
   */
  serverTime: Instant,
});
export type BakerySettings = z.infer<typeof BakerySettings>;

/**
 * The body BOTH mounts serving one shop's settings take: `PATCH /b/settings`
 * from the counter, and `PATCH /c/locations/:locationId/settings` from the
 * house's own portal.
 *
 * Stated here once, beside the record it edits, because the two are the same
 * settings seen from two places - and because it was written out character for
 * character in each module until the audit's finding 54. Four fields are
 * omitted rather than merely ignored: `locationId` identifies the shop (the
 * path on `/c`, the device session on `/b`), `closures` have four endpoints of
 * their own, `immediateAcceptTimeoutMinutes` is administered by operations
 * and read-only at the counter (BAK-003), and `serverTime` is the instant of
 * the READ, which no caller sets.
 *
 * Through `patchSchema`, never `.partial()`: a `.default()` added to
 * `BakerySettings` above would otherwise survive into this body and arrive on
 * every request whatever the caller sent, in TWO places, with nothing shared to
 * fix (see the header of `../patch`).
 */
export const BakerySettingsPatch = patchSchema(
  BakerySettings.omit({
    locationId: true,
    closures: true,
    immediateAcceptTimeoutMinutes: true,
    /* The order camera is the house's and operations' to set, never the
       counter's own (founder, 2026-10-08). */
    bagPhotoPolicy: true,
    serverTime: true,
  }),
);
export type BakerySettingsPatch = z.infer<typeof BakerySettingsPatch>;

/**
 * The same settings as the HOUSE edits them on `PATCH /c/locations/:id/settings`:
 * everything the counter may set, plus the order camera's `bagPhotoPolicy`,
 * which the counter may not. One builder, so a `.default()` added to
 * `BakerySettings` is stripped here exactly as it is above.
 */
export const ShopSettingsPatch = patchSchema(
  BakerySettings.omit({
    locationId: true,
    closures: true,
    immediateAcceptTimeoutMinutes: true,
    serverTime: true,
  }),
);
export type ShopSettingsPatch = z.infer<typeof ShopSettingsPatch>;

export const getSettings = defineEndpoint({
  method: 'GET',
  path: '/b/settings',
  mount: 'bakery',
  summary: 'Pickup windows, closures and acceptance (BAK-010, BAK-027)',
  response: BakerySettings,
});

export const updateSettings = defineEndpoint({
  method: 'PATCH',
  path: '/b/settings',
  mount: 'bakery',
  summary: 'Change the pickup windows; re-derives guest windows from the next service on (BAK-027)',
  body: BakerySettingsPatch,
  idempotent: false,
  response: BakerySettings,
});

export const addClosure = defineEndpoint({
  method: 'POST',
  path: '/b/closures',
  mount: 'bakery',
  summary: 'Record a closure date range - a long holiday, a works week (BAK-010)',
  body: ClosureRangeBody,
  idempotent: true,
  response: ClosureRangeResult,
});

export const deleteClosure = defineEndpoint({
  method: 'DELETE',
  path: '/b/closures/:id',
  mount: 'bakery',
  summary: 'Remove a recorded closure (BAK-010)',
  params: z.object({ id: Uuid }),
  idempotent: false,
  response: Ok,
});

/**
 * One day on the calendar (BAK-047), and the reason a calendar is possible at
 * all.
 *
 * Closures are stored as RANGES and stay ranges - a works week is one row, not
 * eleven - but a calendar toggles a DAY. The split and merge between the two is
 * the platform's arithmetic and not a client's: the handheld cannot be
 * hot-fixed, and the tablet and the partner portal draw the same grid, so three
 * implementations of one rule is where two screens start disagreeing about which
 * days a shop is shut.
 *
 * A PUT of a state rather than an instruction, so it is idempotent in both
 * directions: closing a day already shut answers 200 and writes nothing. That is
 * what makes the retry after a dropped connection on a counter's wifi a no-op
 * instead of a second range.
 *
 * The reply carries the location's WHOLE closure list, so the grid repaints from
 * one answer with no refetch and no client-side merge.
 */
export const setClosureDay = defineEndpoint({
  method: 'PUT',
  path: '/b/closures/day',
  mount: 'bakery',
  summary: 'Close or re-open one day; splits and merges the stored ranges (BAK-047)',
  body: ClosureDayBody,
  idempotent: true,
  response: ClosureDayResult,
});

/**
 * BAK-011: preparation capacity is ONE rate - max courier pickups per hour,
 * default 20 (one every three minutes). A guest window whose pickup lands in
 * an hour that already carries that many orders is not offered (RTE-008).
 */
export const getCapacity = defineEndpoint({
  method: 'GET',
  path: '/b/capacity',
  mount: 'bakery',
  summary: 'The preparation rate: max courier pickups per hour (BAK-011, RTE-008)',
  query: z.object({ serviceDay: ServiceDay.optional() }),
  response: z.object({
    serviceDay: ServiceDay,
    maxPickupsPerHour: z.int().min(1).max(240),
  }),
});

/**
 * The `/c` twin is `partner.updateLocationCapacity`, and the pair must answer
 * the same way (refactoring audit finding 42): identical body, identical
 * response, and both handlers call the same `setPreparationCapacity`. It said
 * `true` here and nothing there, so the counter had to send an Idempotency-Key
 * for this write and the house's own portal was refused for sending one.
 *
 * `false` on both, because the write has nothing a key would protect: it sets
 * one number, last write wins, and a replay of the same body sets the same
 * number. The routes that ARE `true` on this mount are the ones a counter
 * replays after a dropped answer (BAK-009) - accept, reject, ready, delay,
 * collected, stock, pause - where a second run meets the compare-and-swap guard
 * and 409s an order that already moved. Capacity is not one of them, and the
 * native handheld never calls this route at all.
 */
export const updateCapacity = defineEndpoint({
  method: 'PATCH',
  path: '/b/capacity',
  mount: 'bakery',
  summary: 'Set the preparation rate; windows close when an hour is full (BAK-011)',
  body: z.object({ maxPickupsPerHour: z.int().min(1).max(240) }),
  idempotent: false,
  response: Ok,
});

/**
 * The counter's report (BAK-014). Two kinds of figure on one reply, and only the
 * first kind reads `from`/`to`:
 *
 * - PERIOD: `orders`, `rejected`, `cancelled`, `delayedOrders`.
 * - PERMANENT (founder, 2026-09-15): `payable`, `preparationMinutes` /
 *   `preparationMeasured` and `ratings` are standing facts about the shop and
 *   answer the same whatever range is asked.
 */
export const getReports = defineEndpoint({
  method: 'GET',
  path: '/b/reports',
  mount: 'bakery',
  summary:
    'Orders, rejections, what is payable, preparation time and guest ratings (BAK-014, GST-038)',
  query: z.object({ from: ServiceDay, to: ServiceDay }),
  response: z.object({
    orders: z.int().nonnegative(),
    rejected: z.int().nonnegative(),
    /**
     * BAK-043: orders this counter accepted and then GAVE BACK, beside the ones
     * it refused before accepting. Two different facts about a house and two
     * different costs - a refusal is reallocated, a cancellation is a guest
     * whose breakfast was promised and then withdrawn - so they are counted
     * apart rather than summed into one "did not bake it" number.
     */
    cancelled: z.int().nonnegative(),
    /**
     * PERMANENT. The baker's own Stripe balance, available plus pending, as ONE
     * figure (docs/finance/payments-and-payouts.html#accounts): what its orders have
     * moved into its balance and no payout has taken to the bank yet. Read
     * from Stripe; zero while the baker has no verified account. SIGNED: a
     * refund reversed after a payout can take the balance below zero, and a
     * tile that clamped it would hide what the baker owes. Nothing to do with
     * `from`/`to`.
     *
     * NULL when Stripe could not be read: the figure is unknown, and the
     * counter draws a dash rather than a zero that is not true.
     *
     * It is the only money value on this report. `merchandise` sat beside it
     * once and stated the platform's cut to anyone who can subtract - a bakery
     * is told what we owe it and never what we sold its goods for (BAK-029,
     * founder decision 2026-08-20).
     */
    payable: z.int().nullable(),
    /**
     * PERMANENT. Whether the counter shows the tile at all: false when the
     * baker has more than one shop, because the balance is the whole baker's
     * and one counter of a chain would print all of its shops' money.
     */
    payableShown: z.boolean(),
    currency: CurrencyCode,
    /**
     * BAK-014: how long the platform reckons this counter takes to bag an
     * order - and it is THE SAME NUMBER the courier is booked against and the
     * guest is quoted from, never a second statistic about it.
     *
     * It was the median of this counter's spans, which was a report rather than
     * the figure in force: the platform promises on the p75 (the loss is
     * skewed - see `counter-timing.ts`), so a baker read nine minutes on the
     * glass while the rider was coming for fourteen, and an operator tuned the
     * hourly cap (BAK-011) from a different statistic than the one the promise
     * used. One question, one answer, on the screen as in the booking.
     *
     * NEVER NULL, because the platform is always using something: this counter's
     * own p75 once it has bagged enough orders for the figure to mean anything,
     * and the platform seed until then. Which of the two is
     * `preparationMeasured`.
     */
    preparationMinutes: z.number().positive(),
    /**
     * False while the figure above is the platform seed rather than this
     * counter's own measurement.
     *
     * On the wire rather than left to the reader, because "ten minutes" and
     * "ten minutes, assumed" are different claims and the screens say so. A
     * counter that has never marked a bag ready is not a ten-minute counter -
     * it is a counter nobody has timed.
     */
    preparationMeasured: z.boolean(),
    /**
     * How many of the period's ORDERS the counter took more time on (BAK-040) -
     * orders delayed at least once, not delays taken: the delay repeats, and a
     * house reading its own habit wants to know how many breakfasts moved.
     *
     * Deliveroo's own guidance for the same control is "only use it when you
     * need to", and a control used sparingly is invisible while a control used
     * every morning is a capacity problem (BAK-011) nobody has looked at.
     * Counted here so the house sees its own habit; operations reads the same
     * events.
     */
    delayedOrders: z.int().nonnegative(),
    /**
     * PERMANENT. The guests' PRODUCTS score for this shop since it first
     * traded: the average stars and how many ratings it is over, across every
     * order the shop made, storefront mode A and mode B alike - the same
     * attribution the Internal scorecard and ratings list use.
     * `products` is null while `count` is 0. The delivery rating is the
     * courier's, not the shop's, and is not on this report.
     */
    ratings: z.object({
      products: z.number().min(0).max(5).nullable(),
      count: z.int().nonnegative(),
    }),
  }),
});

export const listLocations = defineEndpoint({
  method: 'GET',
  path: '/b/locations',
  mount: 'bakery',
  summary: 'Locations this staff member may operate (BAK-028)',
  query: PageRequest,
  response: pageOf(
    z.object({
      id: Uuid,
      name: z.string().max(160),
      city: z.string().max(100),
      active: z.boolean(),
    }),
  ),
});
